import { useEffect, useRef, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { RUSSIAN_LANGUAGE } from "../../api/config"
import { staticFileUrl } from "../../api/http"
import { keys } from "../../api/keys"
import { groupWrites, imageWrites } from "../../api/mutations"
import { useGroupImages, useImageTypes, useLanguages } from "../../api/queries"
import type { NamedImage, ProductGroup } from "../../api/types"
import type { GroupRights } from "../../auth/rights"
import { ActionNotes, Note } from "../../components/Note"
import { useAction } from "../../hooks/useAction"
import { useConfirm } from "../../hooks/useConfirm"
import { useEscape } from "../../hooks/useEscape"
import PictogramsPanel from "./PictogramsPanel"
import { useWriter } from "./useWriter"

/** YouTube-ссылка: добавляется другим методом (`/api/images/video`), у
 *  латвийских групп не встречается ни разу — в выбор типа не попадает. */
const YOUTUBE_TYPE = 15

/** Вкладка «Описание»: тексты карточки на выбранном языке и её фотографии. */
export default function DescriptionTab({
	group,
	languageId,
	rights,
}: {
	group: ProductGroup
	languageId: number
	rights: GroupRights
}) {
	const languages = useLanguages()
	const images = useGroupImages(group.id)
	const imageTypes = useImageTypes()

	const [zoom, setZoom] = useState<string | null>(null)
	useEscape(!!zoom, () => setZoom(null))

	const client = useQueryClient()
	const action = useAction({
		after: () =>
			client.invalidateQueries({ queryKey: keys.groupImages(group.id) }),
	})
	const { ask, dialog } = useConfirm()
	const [newType, setNewType] = useState<number | null>(null)
	const [fileName, setFileName] = useState("")
	const fileInput = useRef<HTMLInputElement>(null)
	/** Запись в базе есть, а файла на шаре нет — GoodSync ещё не довёз. */
	const [missing, setMissing] = useState<string[]>([])
	/** Метка последнего обновления фото: новый адрес — браузер не возьмёт картинку из памяти. */
	const [stamp, setStamp] = useState(0)
	const [refreshing, setRefreshing] = useState(false)
	const photoUrl = (imageUrl: string) =>
		staticFileUrl(imageUrl) + (stamp ? `?v=${stamp}` : "")

	const languageName =
		languages.data?.find(item => item.id === languageId)?.name.toLowerCase() ??
		`язык ${languageId}`

	const typeName = (typeId: number) =>
		imageTypes.data?.find(type => type.id === typeId)?.name ?? `тип ${typeId}`

	/** Бэк держит одно фото на тип — занятые типы в выбор не попадают. */
	const freeTypes = (imageTypes.data ?? []).filter(
		type =>
			type.id !== YOUTUBE_TYPE &&
			!images.data?.some(image => image.typeId === type.id)
	)

	const busy = action.busy || images.isFetching

	function add() {
		if (newType === null || !fileName) return
		const type = newType
		const name = fileName
		setNewType(null)
		setFileName("")
		if (fileInput.current) fileInput.current.value = ""
		void action.run(() => imageWrites.add(group.id, type, name))
	}

	async function remove(image: NamedImage) {
		const ok = await ask({
			title: `Убрать «${typeName(image.typeId)}» у группы?`,
			text: `Файл ${image.name} на хранилище останется — вернуть фото можно, добавив тот же файл заново.`,
			confirm: "Убрать",
			danger: true,
		})
		if (ok) void action.run(() => imageWrites.remove(group.id, image.typeId))
	}

	/** Файл заменили на хранилище под тем же именем — адрес не изменился, и
	 *  кэши узла (90 дней) и браузера (30) показывают старый. `reload` идёт
	 *  мимо кэша браузера и шлёт `Pragma: no-cache`, по которому nginx узла
	 *  тоже идёт в РФ (`deploy/nginx/conf.d/riga.conf`); оба кэша
	 *  перезаписываются свежим файлом. */
	async function refresh() {
		setRefreshing(true)
		await Promise.all(
			(images.data ?? [])
				.filter(image => !!image.imageUrl)
				.map(image =>
					fetch(staticFileUrl(image.imageUrl), { cache: "reload" }).catch(() => {})
				)
		)
		setMissing([])
		setStamp(Date.now())
		setRefreshing(false)
	}

	return (
		<div className="split">
			<TextsPanel
				group={group}
				languageId={languageId}
				languageName={languageName}
				rights={rights}
			/>

			<div className="split__side">
			<div className="panel">
				<div className="panel__head">
					<span>Фото {images.data ? `· ${images.data.length}` : ""}</span>
					<span className="panel__spacer" />
					{!rights.images && <span className="muted">только просмотр</span>}
					<button
						className="cat-mini"
						disabled={refreshing || !images.data?.length}
						title="Обновить фото с хранилища — если файл заменили под тем же именем"
						onClick={() => void refresh()}
					>
						<svg
							viewBox="0 0 24 24"
							width="14"
							height="14"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
							aria-hidden="true"
							style={{ display: "block", margin: "auto" }}
						>
							<path d="M21 12a9 9 0 1 1-2.64-6.36L21 8" />
							<path d="M21 3v5h-5" />
						</svg>
					</button>
				</div>
				<div className="panel__body">
					<ActionNotes action={action} />
					{images.isLoading && <span className="spinner" />}
					{images.data?.length === 0 && (
						<div className="muted">Фотографий нет</div>
					)}
					<div className="gallery">
						{(images.data ?? [])
							.filter(image => !!image.imageUrl)
							.map(image => (
								<div className="gallery__item" key={image.imageUrl}>
									{missing.includes(image.imageUrl) ? (
										<div
											className="gallery__missing"
											title={`${image.name} не найден на хранилище`}
										>
											файла ещё нет на хранилище
										</div>
									) : (
										<img
											src={photoUrl(image.imageUrl)}
											alt={image.name}
											loading="lazy"
											onClick={() => setZoom(photoUrl(image.imageUrl))}
											onError={() =>
												setMissing(list => [...list, image.imageUrl])
											}
										/>
									)}
									<div
										className="gallery__cap"
										title={`${typeName(image.typeId)} · ${image.name}`}
									>
										{typeName(image.typeId)}
									</div>
									{rights.images && (
										<button
											className="cat-mini cat-mini--danger gallery__remove"
											disabled={busy}
											title="Убрать фото у группы"
											onClick={() => void remove(image)}
										>
											✕
										</button>
									)}
								</div>
							))}
					</div>

					{dialog}

					{rights.images && (
						<div className="photo-add">
							<select
								className="select"
								disabled={busy || freeTypes.length === 0}
								value={newType ?? ""}
								onChange={event =>
									setNewType(event.target.value ? Number(event.target.value) : null)
								}
							>
								<option value="">
									{freeTypes.length === 0 ? "— все типы заняты —" : "— тип фото —"}
								</option>
								{freeTypes.map(type => (
									<option key={type.id} value={type.id}>
										{type.name}
									</option>
								))}
							</select>
							<input
								ref={fileInput}
								type="file"
								accept=".jpg,.jpeg,.png,.gif"
								disabled={busy}
								onChange={event =>
									setFileName(event.target.files?.[0]?.name ?? "")
								}
							/>
							<button
								className="btn"
								disabled={busy || newType === null || !fileName}
								onClick={add}
							>
								Добавить
							</button>
						</div>
					)}
				</div>
			</div>

				<PictogramsPanel
					group={group}
					languageId={languageId}
					rights={rights}
				/>
			</div>

			{zoom && (
				<div
					className="lightbox"
					role="dialog"
					aria-modal="true"
					aria-label="Просмотр фотографии"
					title="Esc или клик — закрыть"
					onClick={() => setZoom(null)}
				>
					<img src={zoom} alt="" />
				</div>
			)}
		</div>
	)
}

function TextsPanel({
	group,
	languageId,
	languageName,
	rights,
}: {
	group: ProductGroup
	languageId: number
	languageName: string
	rights: GroupRights
}) {
	const writer = useWriter(group.id)
	const [draft, setDraft] = useState(group)

	// после сохранения и смены языка карточка приезжает заново
	useEffect(() => setDraft(group), [group])

	const dirty =
		draft.name !== group.name ||
		draft.description !== group.description ||
		draft.descriptionWeb !== group.descriptionWeb ||
		draft.isDescriptionChecked !== group.isDescriptionChecked ||
		draft.isImageChecked !== group.isImageChecked

	const canSave = rights.name || rights.description || rights.checks
	const same = (draft.description ?? "") === (draft.descriptionWeb ?? "")

	return (
		<div className="panel texts-panel">
			<div className="panel__head">Тексты · {languageName}</div>
			<div className="panel__body">
				<ActionNotes action={writer} />

				{languageId === RUSSIAN_LANGUAGE && !rights.name && (
					<Note tone="hint">
						Выбран русский язык — правка заблокирована. Переключите язык в
						шапке.
					</Note>
				)}

				<div className="field">
					<label className="field__label">Название</label>
					<input
						className="input"
						value={draft.name ?? ""}
						disabled={!rights.name}
						onChange={event => setDraft({ ...draft, name: event.target.value })}
					/>
				</div>
				{/* Описания идут парой, а не столбиком: они почти всегда совпадают,
				    рядом это видно сразу, и панель перестаёт занимать весь экран.
				    На узком окне колонки схлопываются в одну. */}
				<div className="texts-two">
					<div className="field">
						<label className="field__label">Описание (каталог)</label>
						<textarea
							className="textarea"
							rows={6}
							value={draft.description ?? ""}
							disabled={!rights.description}
							onChange={event =>
								setDraft({ ...draft, description: event.target.value })
							}
						/>
					</div>
					<div className="field">
						<label className="field__label">
							Описание (сайт)
							{same && <span className="muted"> · совпадает с каталогом</span>}
							{!same && rights.description && (
								<button
									type="button"
									className="linklike"
									title="Скопировать описание каталога в описание сайта"
									onClick={() =>
										setDraft({ ...draft, descriptionWeb: draft.description })
									}
								>
									← скопировать из каталога
								</button>
							)}
						</label>
						<textarea
							className="textarea"
							rows={6}
							value={draft.descriptionWeb ?? ""}
							disabled={!rights.description}
							onChange={event =>
								setDraft({ ...draft, descriptionWeb: event.target.value })
							}
						/>
					</div>
				</div>

				<div className="texts-foot">
					<div className="checkline">
						<label>
							<input
								type="checkbox"
								checked={draft.isDescriptionChecked}
								disabled={!rights.checks}
								onChange={event =>
									setDraft({
										...draft,
										isDescriptionChecked: event.target.checked,
									})
								}
							/>
							описание проверено
						</label>
						<label>
							<input
								type="checkbox"
								checked={draft.isImageChecked}
								disabled={!rights.checks}
								onChange={event =>
									setDraft({ ...draft, isImageChecked: event.target.checked })
								}
							/>
							фото проверены
						</label>
					</div>

					<span className="texts-foot__spacer" />

					{dirty && (
						<span className="muted hint-inline">есть несохранённые правки</span>
					)}
					<button
						className="btn btn--primary"
						disabled={!canSave || !dirty || writer.busy}
						onClick={() =>
							void writer.run(() => groupWrites.update(draft, languageId))
						}
					>
						Сохранить
					</button>
				</div>
			</div>
		</div>
	)
}
